
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Building } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useCompanyRequestStore } from '@/lib/stores/companyRequestStore';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Button } from '@/components/ui/button';
import { australianLocations } from '@/components/company/CompanyFormSchema';

// Updated schema to make website optional since it's not in the database
const formSchema = z.object({
  companyName: z.string().min(2, { message: 'Company name must be at least 2 characters' }),
  location: z.string().min(2, { message: 'Location must be at least 2 characters' }),
  contactEmail: z.string().email({ message: 'Please enter a valid email address' }),
  website: z.string().url({ message: 'Please enter a valid URL' }).optional(), // Make website optional
  additionalInfo: z.string().optional(),
});

export type CompanyRequestFormValues = z.infer<typeof formSchema>;

const RequestCompanyForm = () => {
  const navigate = useNavigate();
  const { addRequest } = useCompanyRequestStore();
  const { toast } = useToast();

  const form = useForm<CompanyRequestFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      location: '',
      contactEmail: '',
      website: '',
      additionalInfo: '',
    },
  });

  const onSubmit = async (data: CompanyRequestFormValues) => {
    const requestData = {
      companyName: data.companyName,
      location: data.location,
      contactEmail: data.contactEmail,
      additionalInfo: data.additionalInfo,
      // Website field is omitted since it's not in the database schema
    };
    
    const result = await addRequest(requestData);
    
    if (result) {
      toast({
        title: 'Request submitted',
        description: 'Thank you! Your company request has been submitted.',
      });
      navigate('/companies');
    } else {
      toast({
        title: 'Request failed',
        description: 'There was an error submitting your request. Please try again.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm p-8">
      <div className="flex items-center justify-center mb-6">
        <div className="w-16 h-16 bg-redflags-light/20 rounded-full flex items-center justify-center">
          <Building className="w-8 h-8 text-redflags-red" />
        </div>
      </div>
      
      <h1 className="text-2xl font-bold text-center text-redflags-dark mb-2">
        Request New Company
      </h1>
      <p className="text-redflags-gray text-center mb-8">
        Help us grow our database by suggesting a company that's missing
      </p>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name*</FormLabel>
                <FormControl>
                  <Input placeholder="Enter company name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location*</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {australianLocations.map((location) => (
                        <SelectItem key={location} value={location}>
                          {location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Website (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    type="url" 
                    placeholder="https://example.com" 
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="contactEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Email*</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="your-email@example.com" 
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="additionalInfo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Additional Information</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Any other details about the company that might be helpful..." 
                    className="min-h-[100px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button 
            type="submit" 
            className="w-full bg-redflags-red hover:bg-redflags-red/90 text-white"
          >
            Submit Request
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default RequestCompanyForm;
